/**
 * ActionDetailChart Component
 *
 * Wrapper around useActionDotChart D3 hook.
 * Renders the SVG court with action dots.
 * Shows empty state when filters eliminate all actions.
 *
 * Parallels ShotDetailChart but simpler — no hexbin, no tooltip, no selection.
 */

import { Box, Typography } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { ActionDetailChartProps } from "./types";
import { useActionDotChart } from "./useActionDotChart";
import { getVisibleCourtLength, COURT_CONSTANTS } from "../ShotDetailModal/types";

export default function ActionDetailChart({
  actions,
  lineups,
  filters,
  width: propWidth,
  height: propHeight,
  showCollegeLine = false,
  highlightedActions = null,
}: ActionDetailChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Responsive width
  const width = propWidth ?? (isMobile ? 300 : 520);

  // Calculate height from aspect ratio (same as ShotDetailChart)
  const aspectRatio = getVisibleCourtLength() / COURT_CONSTANTS.COURT_WIDTH;
  const height = propHeight ?? Math.round(width * aspectRatio);

  const { svgRef, filteredActionCount } = useActionDotChart(
    actions,
    filters,
    lineups,
    { width, height, showCollegeLine, highlightedActions }
  );

  return (
    <Box sx={{ position: "relative" }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{
          backgroundColor: "#f8f8f8",
          borderRadius: 4,
          display: "block",
        }}
      />

      {/* Empty state overlay when filters hide all actions */}
      {filteredActionCount === 0 && actions.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: 2,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No actions match current filters
          </Typography>
        </Box>
      )}
    </Box>
  );
}
